/**
 * A collection of misc. utility functions.
 */

import * as moment from 'moment';
import axios from 'axios';
import _ from 'lodash';

/**
 * Get a moment duration object.
 *
 * @param date
 */
export function durationObject(date) {
    let now = moment();
    let ms = now.diff(date);
    return moment.duration(ms);
}

/**
 * Recursively find an object.
 *
 * @param {Array} array
 * @param {string} key
 * @param {mixed} value
 * @param {string} childrenName
 */
export function findRecursive(array, key, value, childrenName = 'children', strictCheck = true) {
    let o = null;

    if( !Array.isArray(array) ){
        return o;
    }
    array.some(function iter(a) {
        if ((strictCheck && a[key] === value) || (! strictCheck && a[key] == value)) {
            o = a;
            return true;
        }

        return Array.isArray(a[childrenName]) && a[childrenName].some(iter);
    });

    return o;
}

export function isExist (obj) {
    return typeof obj !== 'undefined' && obj !== null;
}

/**
 * Generate a unique id.
 *
 * @param {string} prefix
 */
export function uniqueId(prefix = '') {
    return `${prefix}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Perform an ajax GET request to the server.
 *
 * @param {String} endPoint
 * @param {Object|null} params
 * @param {Object|null} config
 */
export function axiosGet (endPoint, params = null, config = {}) {
    if (params) {
        params = {
            params
        };
    }

    return axiosSubmit('get', endPoint, params, config);
}

/**
 * Post an ajax request to the server.
 *
 * @param {String} endPoint
 * @param {Object|null} params
 * @param {Object|null} config
 */
export function axiosPost (endPoint, params = null, config = {}) {
    return axiosSubmit('post', endPoint, params, config);
}

/**
 * Submit an ajax request to the server.
 *
 * @param {String} requestType get or post
 * @param {String} endPoint
 * @param {Object|null} payload
 * @param {Object|null} config
 */
export function axiosSubmit (requestType, endPoint, payload = null, config = {}) {
    return new Promise((resolve, reject) => {
        axios[requestType](endPoint, payload, config)
            .then(({data}) => {
                resolve(data);
            })
            .catch(error => {
                reject(error);
            });
    });
}

/**
 * Deep copy the given object.
 *
 * @param {Object} obj
 */
export function deepCopy (obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    const copy = Array.isArray(obj) ? [] : {};

    Object.keys(obj).forEach(key => {
        copy[key] = deepCopy(obj[key]);
    });

    return copy;
}

/**
 * Determine if the given object has any files.
 *
 * @param {Object} obj
 */
export function hasFile (obj) {
    return Object.keys(obj).some(key =>
        obj[key] instanceof Blob || obj[key] instanceof FileList
    );
}

/**
 * Returns the type of anything.
 *
 * @param {Object} any
 */
export function getType (any) {
    return toString.call(any).slice(8, -1);
}

/**
 * Converts path to FormData name
 */

/**
 * Convert the given object to a FormData instance.
 *
 * @param {Object} obj
 */
export function toFormData (obj) {
    const data = new FormData();

    Object.keys(obj).forEach(key => {
        const value = obj[key];

        if (value instanceof FileList) {
            for (let i = 0; i < value.length; i++) {
                data.append(`${key}[]`, value.item(i));
            }
        } else {
            if (_.isArray(value)) {
                try {
                    value.forEach(v => {
                        data.append(`${key}[]`, v);
                    });
                } catch (e) {}
            } else if (_.isObject(value)) {
                objectToFormData(data, value);
            } else {
                data.append(key, value);
            }
        }
    });

    return data;
}

/**
 * Convert an object to FormData
 *
 * @param {FormData} fd
 * @param {Object} obj
 * @param {String|null} namespace
 */
function objectToFormData (fd, obj, namespace) {
    let formKey;

    for (let property in obj) {
        if (namespace) {
            formKey = `${namespace}[${property}]`;
        } else {
            formKey = property;
        }

        // If the property is an object, but not a File, call this function recursively
        if (_.isObject(obj[property]) && ! obj[property] instanceof File) {
            objectToFormData(fd, obj[property], property);
        } else {
            // If it's a string or a File object
            fd.append(formKey, obj[property]);
        }
    }

    return fd;
}


if (! Array.isArray) {
    Array.isArray = arg => Object.prototype.toString.call(arg) === '[object Array]';
}

export const isArray = Array.isArray;

export const isObject = obj => {
    return obj && ({}).toString.call(obj) === '[object Object]';
};